import { t } from '@/i18n';
import type { Provider } from '@/types';

export type ContentPart = { type: 'text'; text: string } | { type: 'image'; mimeType: string; base64: string };

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: ContentPart[];
}

/** What a provider reports besides the text: token usage, and for web-enabled calls the searches it ran and the pages it cited. */
export interface ChatMeta {
  inputTokens?: number;
  outputTokens?: number;
  searches?: number;
  queries?: string[];
  sources?: { url: string; title?: string }[];
  searchSuggestionsHtml?: string; // Gemini only: Google's required attribution block
}

export interface ChatRequest {
  system: string;
  messages: ChatMessage[];
  model: string;
  apiKey: string;
  signal?: AbortSignal;
  jsonMode?: boolean;
  /** Lets the provider search the web server-side (Claude web_search, OpenAI Responses web_search, Gemini google_search). */
  webSearch?: { maxUses: number };
  onDelta?: (text: string) => void;
  onMeta?: (meta: ChatMeta) => void;
}

export interface LLMAdapter {
  provider: Provider;
  chat(req: ChatRequest): Promise<string>;
  listModels(apiKey: string, signal?: AbortSignal): Promise<string[]>;
}

export type LLMErrorCode = 'invalid_key' | 'quota' | 'rate_limited' | 'context_too_long' | 'network' | 'bad_json' | 'aborted' | 'upstream';

export class LLMError extends Error {
  constructor(
    public readonly code: LLMErrorCode,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'LLMError';
  }
}

export function describeError(e: unknown): string {
  if (e instanceof LLMError) return e.message;
  if (e instanceof DOMException && e.name === 'AbortError') return t('llm.aborted');
  if (e instanceof Error) return e.message || t('common.unknownError');
  return String(e);
}
